import React, { useContext, useState } from 'react';
import { Box, Button, Checkbox, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography, Alert, MenuItem, Tooltip } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import Header from '../Components/layout/Header';
import { API } from '../conf';
import { Context } from '../App';

const norm = s => (s || '').toLowerCase().trim()
const isDuplicate = (d1, d2) => norm(d1.nom) === norm(d2.nom) && norm(d1.prenom1) === norm(d2.prenom1)

export default function Cerfa() {
  const { exercice } = useContext(Context);

  const [selectedExercice, setSelectedExercice] = useState(exercice || new Date().getFullYear())
  const [searchInput, setSearchInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [donateurs, setDonateurs] = useState([]);
  const [anchorId, setAnchorId] = useState(null);   // the primary selected donateur
  const [selectedIds, setSelectedIds] = useState([]); // anchor + selected duplicates
  const [message, setMessage] = useState('');

  const currentYear = new Date().getFullYear()
  const exerciceOptions = Array.from({length: 5}, (_, i) => currentYear - i)

  const anchor = donateurs.find(d => d._id === anchorId) || null

  const handleSearch = () => {
    setMessage('');
    const text = searchInput.trim();
    if (!text) { setMessage('Saisissez un nom, prénom, téléphone ou e-mail.'); return; }
    setLoading(true);
    setAnchorId(null);
    setSelectedIds([]);
    fetch(API.concat(`/donateurs/search/${encodeURIComponent(text)}`), {
      headers: { Accept: 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') }
    })
      .then(async (response) => {
        const token = response.headers.get('x-auth-token');
        if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token);
        if (response.status === 401) { sessionStorage.clear(); window.location.reload(); return; }
        if (response.status === 200) {
          const json = await response.json();
          setDonateurs(json);
          if (json.length === 0) setMessage('Aucun donateur trouvé.');
        } else {
          setMessage('Erreur lors de la recherche.');
        }
      })
      .catch(() => setMessage('Erreur réseau.'))
      .finally(() => setLoading(false));
  };

  const handleClick = (d) => {
    if (!anchorId) {
      // First click: set as anchor
      setAnchorId(d._id);
      setSelectedIds([d._id]);
    } else if (anchorId === d._id) {
      // Click on anchor: deselect all
      setAnchorId(null);
      setSelectedIds([]);
    } else {
      // Only allow toggling duplicates of anchor
      if (!anchor || !isDuplicate(anchor, d)) return;
      setSelectedIds(old => old.includes(d._id) ? old.filter(v => v !== d._id) : [...old, d._id]);
    }
  };

  const handleGenerate = async () => {
    setMessage('');
    if (!anchorId) { setMessage('Sélectionnez au moins un donateur.'); return; }
    setGenerating(true);
    // Primary donateur first, then duplicates
    const anchorDonateur = donateurs.find(d => d._id === anchorId);
    const others = donateurs.filter(d => selectedIds.includes(d._id) && d._id !== anchorId);
    const donateurNums = [anchorDonateur.num, ...others.map(d => d.num)];
    try {
      const response = await fetch(API.concat('/cerfa'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + sessionStorage.getItem('token') },
        body: JSON.stringify({ donateurNums, exercice: selectedExercice })
      });
      const token = response.headers.get('x-auth-token');
      if (token && token !== sessionStorage.getItem('token') && token !== '') sessionStorage.setItem('token', token);
      if (response.status === 401) { sessionStorage.clear(); window.location.reload(); return; }
      if (!response.ok) { setMessage(await response.text()); return; }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Attestation_fiscale_${anchorDonateur.nom}_${selectedExercice}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      setMessage('Erreur lors de la génération.');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Header />
      <Box display='flex' flexDirection='column' gap={2} padding={3}>
        <Typography variant='h4'>Attestations fiscales</Typography>
        <Typography variant='body2' color='text.secondary'>
          Recherchez un donateur. Cliquez sur lui pour le sélectionner, puis cochez ses éventuels doublons à fusionner. Les dons de tous les doublons sélectionnés seront cumulés dans l'attestation.
        </Typography>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='center'>
          <TextField
            select size='small' label='Exercice'
            value={selectedExercice}
            onChange={e => { setSelectedExercice(e.target.value); setAnchorId(null); setSelectedIds([]); setDonateurs([]); }}
            sx={{ minWidth: 120 }}
          >
            {exerciceOptions.map(y => <MenuItem key={y} value={y}>{y}</MenuItem>)}
          </TextField>
          <TextField
            label='Nom, prénom, téléphone, e-mail'
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
            size='small' fullWidth
          />
          <Button variant='contained' startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading}>
            Rechercher
          </Button>
        </Stack>

        {message && <Alert severity='info'>{message}</Alert>}

        {loading ? (
          <Box display='flex' justifyContent='center' padding={3}><CircularProgress /></Box>
        ) : donateurs.length > 0 ? (
          <Paper elevation={2}>
            <Box padding={2}>
              <Typography variant='h6'>{donateurs.length} donateur(s) trouvé(s)</Typography>
              {anchor && (
                <Typography variant='body2' color='text.secondary' mt={0.5}>
                  Sélectionné : <strong>{anchor.genre} {anchor.nom} {anchor.prenom1}</strong>
                  {selectedIds.length > 1 ? ` + ${selectedIds.length - 1} doublon(s)` : ''}.
                  Les lignes grisées ne sont pas des doublons.
                </Typography>
              )}
            </Box>
            <TableContainer>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell align='center' sx={{ width: 60 }}></TableCell>
                    <TableCell>Civilité</TableCell>
                    <TableCell>Nom</TableCell>
                    <TableCell>Prénoms</TableCell>
                    <TableCell>Téléphone</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Adresse</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {donateurs.map((d) => {
                    const isSelected = selectedIds.includes(d._id);
                    const isAnchor = d._id === anchorId;
                    const isDisabled = anchorId && !isAnchor && anchor && !isDuplicate(anchor, d);
                    return (
                      <Tooltip
                        key={d._id}
                        title={isDisabled ? 'Ce donateur n\'est pas un doublon du donateur sélectionné' : ''}
                        placement='left'
                      >
                        <TableRow
                          hover={!isDisabled}
                          selected={isSelected}
                          onClick={() => handleClick(d)}
                          sx={{
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            opacity: isDisabled ? 0.35 : 1,
                            backgroundColor: isAnchor ? 'action.selected' : undefined,
                          }}
                        >
                          <TableCell align='center'>
                            <Checkbox
                              checked={isSelected}
                              disabled={isDisabled}
                              onChange={() => handleClick(d)}
                              onClick={e => e.stopPropagation()}
                              size='small'
                            />
                          </TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{d.genre}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{d.nom}</TableCell>
                          <TableCell sx={{ textTransform: 'capitalize' }}>{d.prenom1}{d.prenom2 ? ` ${d.prenom2}` : ''}</TableCell>
                          <TableCell>{d.tel}</TableCell>
                          <TableCell>{d.mail}</TableCell>
                          <TableCell>
                            {d.voie && <Typography variant='body2'>{d.voie}</Typography>}
                            {(d.zip_code || d.ville) && <Typography variant='body2'>{`${d.zip_code || ''} ${(d.ville || '').toUpperCase()}`.trim()}</Typography>}
                          </TableCell>
                        </TableRow>
                      </Tooltip>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : null}

        <Box display='flex' justifyContent='flex-end'>
          <Button
            variant='contained' color='primary'
            startIcon={generating ? <CircularProgress size={16} color='inherit' /> : <PictureAsPdfIcon />}
            onClick={handleGenerate}
            disabled={!anchorId || generating}
          >
            {generating
              ? 'Génération en cours…'
              : selectedIds.length > 1
                ? `Générer l'attestation (${selectedIds.length} doublons fusionnés)`
                : `Générer l'attestation`}
          </Button>
        </Box>
      </Box>
    </div>
  );
}
